import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { SingleSelect } from '@/components/common/SingleSelect';
import {
  MultiSelect,
  MultiSelectOption,
} from '@/components/common/MultiSelect';
import PaginatedGentlyTable, {
  TableColumn,
  type PaginationConfig,
} from '@/components/common/PaginatedGentlyTable';
import { TableSkeleton } from '@/components/common/loading-skeletons/table-skeleton';
import { useExportTable } from '@/hooks/useExportTable';
import {
  useCalculatedBreakdownsExport,
  downloadCalculatedBreakdownsCsv,
} from '@/api/rebates/useRebateDataExports';
import type {
  CalculatedBreakdownExportItem,
  CalculatedBreakdownType,
} from '@erp/shared-types';
import { formatCurrency } from '@erp/shared-utils';
import { DownloadIcon, MagnifyingGlassIcon } from '@phosphor-icons/react';
import useDivisions from '@/api/organization/useDivisions';
import { DatePicker } from '@/components/common/date-pickers/date-picker';
import useRebateAgreements from '@/api/rebates/useRebateAgreements';
import useAllSuppliers from '@/api/accounts-payable/useAllSuppliersQuery';
import useCoreEnums from '@/api/core/core-enums/useCoreEnums';
import { REBATES_SYSTEM_ENUMS } from '@erp/shared-types';

const CalculatedBreakdownsExportTable: React.FC = () => {
  const [shouldFetchData, setShouldFetchData] = useState(false);
  const [isDownloadingCsv, setIsDownloadingCsv] = useState(false);

  const exportTable = useExportTable({
    initialPageSize: 100,
    defaultType: 'estimated',
  });

  // Extract years from date range for agreements query
  const agreementYears = useMemo(() => {
    if (
      !exportTable.filters.dateRange?.from ||
      !exportTable.filters.dateRange?.to
    ) {
      return [];
    }
    const startYear = exportTable.filters.dateRange.from.getUTCFullYear();
    const endYear = exportTable.filters.dateRange.to.getUTCFullYear();
    const years: number[] = [];
    for (let year = startYear; year <= endYear; year++) {
      years.push(year);
    }
    return years;
  }, [exportTable.filters.dateRange]);

  // Fetch available data
  const { data: divisionsData } = useDivisions(true);
  const { data: agreementsData } = useRebateAgreements({
    years: agreementYears,
  });
  const { data: allSuppliers } = useAllSuppliers();
  const { data: agreementTagsEnum } = useCoreEnums({
    name: REBATES_SYSTEM_ENUMS.AGREEMENT_TAGS.name,
    includeOptions: true,
  });
  const { data: supplierTagsEnum } = useCoreEnums({
    name: REBATES_SYSTEM_ENUMS.SUPPLIER_TAGS.name,
    includeOptions: true,
  });

  const divisionOptions = useMemo(() => {
    if (!divisionsData?.divisions) return [];
    return divisionsData.divisions.map((division) => ({
      id: division.id,
      label: division.name,
      value: division.id,
    }));
  }, [divisionsData]);

  const agreementOptions = useMemo(() => {
    if (!agreementsData) return [];
    return agreementsData.map((agreement) => ({
      id: agreement.id,
      label: agreement.name,
      value: agreement.id,
    }));
  }, [agreementsData]);

  const supplierOptions = useMemo(() => {
    if (!allSuppliers) return [];
    return allSuppliers.map((supplier) => ({
      id: supplier.id,
      label: supplier.name,
      value: supplier.id,
    }));
  }, [allSuppliers]);

  const supplierTagOptions = useMemo(() => {
    if (!supplierTagsEnum?.edges?.[0]?.options) return [];
    return supplierTagsEnum.edges[0].options.map((option) => ({
      id: option.id,
      label: option.name || '',
      value: option.id,
    }));
  }, [supplierTagsEnum]);

  const agreementTagOptions = useMemo(() => {
    if (!agreementTagsEnum?.edges?.[0]?.options) return [];
    return agreementTagsEnum.edges[0].options.map((option) => ({
      id: option.id,
      label: option.name || '',
      value: option.id,
    }));
  }, [agreementTagsEnum]);

  const typeOptions = useMemo(
    () => [
      {
        id: 'estimated',
        label: 'Estimated',
        value: 'estimated' as CalculatedBreakdownType,
      },
      {
        id: 'predicted',
        label: 'Predicted',
        value: 'predicted' as CalculatedBreakdownType,
      },
      {
        id: 'reported',
        label: 'Reported',
        value: 'reported' as CalculatedBreakdownType,
      },
    ],
    [],
  );

  const selectedTypeOption = useMemo(() => {
    return exportTable.filters.type
      ? typeOptions.find((opt) => opt.value === exportTable.filters.type) ||
          null
      : null;
  }, [exportTable.filters.type, typeOptions]);

  // Fetch data
  const apiFilters = useMemo(
    () => ({ ...exportTable.buildApiFilters(), includeCodes: true }),
    [exportTable],
  );
  const { data, isLoading, refetch } = useCalculatedBreakdownsExport(
    apiFilters,
    shouldFetchData,
  );

  // Update pagination info when data arrives
  useEffect(() => {
    if (data?.pageInfo) {
      exportTable.pagination.setPaginationInfo({
        totalCount: data.pageInfo.totalCount,
        totalPages: data.pageInfo.totalPages,
        currentPage: data.pageInfo.currentPage,
      });
      exportTable.pagination.setHasNextPage(data.pageInfo.hasNextPage);
    }
  }, [data, exportTable.pagination]);

  // Columns
  const columns: TableColumn<CalculatedBreakdownExportItem>[] = useMemo(
    () => [
      { id: 'date', label: 'Date', sortable: true },
      { id: 'agreementName', label: 'Agreement', sortable: true },
      { id: 'supplierName', label: 'Supplier', sortable: true },
      {
        id: 'supplierCode',
        label: 'Supplier Code',
        sortable: true,
        render: (val) => (val ? String(val) : '-'),
      },
      { id: 'ruleName', label: 'Rule', sortable: true },
      { id: 'divisionName', label: 'Division', sortable: true },
      {
        id: 'divisionCode',
        label: 'Division Code',
        sortable: true,
        render: (val) => (val ? String(val) : '-'),
      },
      { id: 'type', label: 'Calculation Type', sortable: true },
      {
        id: 'rebateAmount',
        label: 'Rebate Amount',
        sortable: true,
        render: (val) => formatCurrency(val as number),
      },
      {
        id: 'cumulativeRebateAmount',
        label: 'Cumulative Rebate',
        sortable: true,
        render: (val) => formatCurrency(val as number),
      },
      {
        id: 'affectedVolume',
        label: 'Affected Volume',
        sortable: true,
        render: (val) => (val !== null ? formatCurrency(val as number) : '-'),
      },
      { id: 'tierApplied', label: 'Tier Applied', sortable: true },
      {
        id: 'isHistorical',
        label: 'Historical',
        sortable: true,
        render: (val) => (val ? 'Yes' : 'No'),
      },
    ],
    [],
  );

  // Pagination config
  const paginationConfig: PaginationConfig | undefined = useMemo(() => {
    if (!shouldFetchData || !data?.pageInfo) return undefined;

    return {
      enabled: true,
      pagination: exportTable.pagination.paginationState,
      onPageSizeChange: exportTable.pagination.setPageSize,
      onPreviousPage: exportTable.pagination.goToPreviousPage,
      onNextPage: exportTable.pagination.goToNextPage,
      onFirstPage: exportTable.pagination.goToFirstPage,
      onLastPage: data.pageInfo.totalPages
        ? () => exportTable.pagination.goToPage(data.pageInfo.totalPages)
        : undefined,
      onGoToPage: exportTable.pagination.goToPage,
      showRowsPerPage: true,
      showPageInfo: true,
      showPageNumbers: true,
      rowsPerPageOptions: [10, 25, 50, 100, 200],
    };
  }, [shouldFetchData, data, exportTable.pagination]);

  const handleFetch = useCallback(() => {
    if (shouldFetchData) {
      refetch();
    } else {
      setShouldFetchData(true);
    }
  }, [shouldFetchData, refetch]);

  const handleDownloadCsv = useCallback(async () => {
    setIsDownloadingCsv(true);
    try {
      await downloadCalculatedBreakdownsCsv(apiFilters);
    } catch (error) {
      console.error('Failed to download CSV:', error);
    } finally {
      setIsDownloadingCsv(false);
    }
  }, [apiFilters]);

  return (
    <Card>
      <CardHeader>
        <CardTitle>Rebate Earnings Export</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="mb-6 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
            <div className="flex flex-col gap-1.5">
              <label className="text-sm font-medium text-foreground">
                Start Date
              </label>
              <DatePicker
                value={exportTable.filters.dateRange?.from}
                onSelect={(date: Date | undefined) => {
                  exportTable.setDateRange({
                    from: date,
                    to: exportTable.filters.dateRange?.to,
                  });
                  exportTable.setAgreements([]);
                }}
                placeholder="Select start date..."
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <label className="text-sm font-medium text-foreground">
                End Date
              </label>
              <DatePicker
                value={exportTable.filters.dateRange?.to}
                onSelect={(date: Date | undefined) => {
                  exportTable.setDateRange({
                    from: exportTable.filters.dateRange?.from,
                    to: date,
                  });
                  exportTable.setAgreements([]);
                }}
                placeholder="Select end date..."
              />
            </div>
            <SingleSelect
              label="Type"
              options={typeOptions}
              value={selectedTypeOption}
              onChange={(option) => {
                exportTable.setType(
                  option ? (option.value as CalculatedBreakdownType) : null,
                );
              }}
              placeholder="Select type..."
              size="sm"
            />
            <MultiSelect
              label="Agreements"
              options={agreementOptions}
              value={exportTable.filters.agreements}
              onChange={exportTable.setAgreements}
              placeholder="Select agreements..."
              searchPlaceholder="Search agreements..."
            />
            <MultiSelect
              label="Agreement Tags"
              options={agreementTagOptions}
              value={exportTable.filters.agreementTags}
              onChange={exportTable.setAgreementTags}
              placeholder="Select tags..."
              searchPlaceholder="Search tags..."
            />
            <MultiSelect
              label="Suppliers"
              options={supplierOptions}
              value={exportTable.filters.suppliers}
              onChange={exportTable.setSuppliers}
              placeholder="Select suppliers..."
              searchPlaceholder="Search suppliers..."
            />
            <MultiSelect
              label="Supplier Tags"
              options={supplierTagOptions}
              value={exportTable.filters.supplierTags}
              onChange={exportTable.setSupplierTags}
              placeholder="Select tags..."
              searchPlaceholder="Search tags..."
            />
            <MultiSelect
              label="Divisions"
              options={divisionOptions}
              value={exportTable.filters.divisions}
              onChange={exportTable.setDivisions}
              placeholder="Select divisions..."
              searchPlaceholder="Search divisions..."
            />
          </div>
          <div className="flex justify-end gap-2">
            <Button
              onClick={handleDownloadCsv}
              disabled={isDownloadingCsv}
              variant="outline"
            >
              <DownloadIcon className="h-4 w-4 mr-2" />
              {isDownloadingCsv ? 'Downloading...' : 'Download CSV'}
            </Button>
            <Button onClick={handleFetch} disabled={isLoading}>
              <MagnifyingGlassIcon className="h-4 w-4 mr-2" />
              {isLoading ? 'Loading...' : 'Fetch Data'}
            </Button>
          </div>
        </div>

        {!shouldFetchData ? (
          <div className="text-center py-12 text-muted-foreground">
            Configure filters above and click "Fetch Data" to load results
          </div>
        ) : isLoading || !data?.items ? (
          <TableSkeleton />
        ) : (
          <PaginatedGentlyTable<CalculatedBreakdownExportItem>
            data={data.items}
            columns={columns}
            isLoading={isLoading}
            searchPlaceholder="Search rebate earnings..."
            emptyMessage="No rebate earnings found"
            showSearch={true}
            showDownload={false}
            downloadFilename="calculated-breakdowns"
            showCount={true}
            itemName="rebate earnings"
            paginationConfig={paginationConfig}
            serverSideSorting={{
              sortBy: exportTable.pagination.sortBy,
              sortDirection:
                exportTable.pagination.sortDirection === 'ASC' ? 'asc' : 'desc',
              onSort: (columnId, direction) => {
                exportTable.pagination.setSorting(
                  columnId,
                  direction === 'asc' ? 'ASC' : 'DESC',
                );
              },
            }}
          />
        )}
      </CardContent>
    </Card>
  );
};

export default CalculatedBreakdownsExportTable;
